import { isNull, isUndefined } from 'lodash'

import { format } from 'date-fns'
import moment from 'moment-timezone'
import { timeFormats } from '../enum/TIME'

export const timeZone = (
  timeZone: string | undefined,
  timeStamp: Date | string | number,
  timeFormat: timeFormats
) => {
  const formattedTime = moment(timeStamp)
    .tz(
      isUndefined(timeZone)
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : timeZone
    )
    .format(timeFormat)
  return formattedTime.replace(/^(\w+)/, (match) => match.slice(0, 3))
}

export function timeZoneOrg(
  timeZone: string | undefined,
  timeStamp: Date | string | number | undefined,
  timeFormat: timeFormats
) {
  if (!timeStamp) {
    return ''
  }

  try {
    const effectiveTimeZone = isUndefined(timeZone)
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : timeZone

    // Use moment.utc() to prevent timezone offset shifting
    const formattedTime = moment
      .utc(timeStamp)
      .tz(effectiveTimeZone, true) // true preserves the UTC time
      .format(timeFormat)

    if (timeFormat.includes('MMMM') || timeFormat.includes('MMM')) {
      return formattedTime.replace(
        /^(\w+)/,
        (match) =>
          match.charAt(0).toUpperCase() + match.slice(1, 3).toLowerCase()
      )
    }

    return formattedTime
  } catch (error) {
    console.error('Error formatting timezone:', error)
    return ''
  }
}

export const convertToUTC = (dateString: string) => {
  const momentDateWithOffset = moment(dateString, 'M/D/YYYY Z')
  const utcDate = momentDateWithOffset.utc()

  const formattedUTCDate = utcDate.toISOString()

  return formattedUTCDate
}

export const getDateString = (date: Date | null, displayTime: boolean) => {
  if (!date) return '--'
  else if (displayTime) {
    return `${format(
      date,
      'dd - M - yyyy'
    )} ${date.getHours()}:${date.getMinutes()}`
  } else {
    return format(date, 'dd - M - yyyy')
  }
}

export const timeMetric = (value: number, metric: string) => {
  if (value === 0) return ''
  //Calculating time in milliseconds
  switch (metric) {
    case 's':
      value *= 1000
      break
    case 'm':
      value *= 60000
      break
    case 'h':
      value *= 3600000
      break
    case 'd':
      value *= 86400000
      break
    default:
      value *= 1
      break
  }
  //converting milliseconds to days if its greater than 365days converting to year
  const days = value / 86400000
  if (days === 365) return `${(days / 365).toFixed(2)} year`
  else if (days > 365 && days / 365 >= 2)
    return `${(days / 365).toFixed(2)} years`
  //converting milliseconds to hour if its greater than 48hr converting to days
  const hr = value / 3600000
  if (hr > 48) return `${days.toFixed(2)} days`
  //converting milliseconds to min if its greater than 120min converting to hours
  const min = value / 60000
  if (min === 1) return `${min.toFixed(2)} min`
  if (min >= 2 && min < 120) return `${min.toFixed(2)} mins`
  if (min >= 120) return `${hr.toFixed(2)} hrs`
  //converting milliseconds to seconds
  const sec = value / 1000
  if (sec === 1) return `${sec.toFixed(2)} sec`
  else if (sec >= 2) return `${sec.toFixed(2)} secs`

  return `${value.toFixed(2)} ms`
}

export const msToTimeUnits = (valueInMs: number) => {
  switch (true) {
    case valueInMs < 1000:
      return `${roundTimeValue(valueInMs)} ms`
    case valueInMs < 60000:
      return `${roundTimeValue(valueInMs / 1000)} sec`
    case valueInMs < 3600000:
      return `${roundTimeValue(valueInMs / 60000)} min`
    default:
      return `${roundTimeValue(valueInMs / 3600000)} hrs`
  }
}
export const roundTimeValue = (value: number, decimalPlaces = 2) => {
  const result = Number(value.toFixed(decimalPlaces))
  const result2 = Number(value.toFixed(0))
  return result === result2 ? result2 : result
}

/**
 * Util function to format give date
 * @param {Date | string} date - date to be formatted
 * @param {string} format - format
 * @returns {string} - formatted date string
 */
export const formatDate = (date: Date | string, format: string): string =>
  moment(date).format(format)

/**
 * Util function to check whether the given date1 is after the given date2
 * @param {Date} date1 - start date
 * @param {Date} date2 - end date
 * @returns {Boolean} - returns true if start date is after end date, else returns false
 */
export const isAfter = (date1: Date, date2: Date): boolean =>
  moment(date1).isAfter(date2)

/**
 * Util function to change a date string to required format
 * @param {string} date - date string
 * @param {string} dateFormat - date format of that date
 * @param {string} requiredFormat - required format
 * @returns {string} - formatted date string
 */
export const formatDateOfDifferentFormat = (
  date: string,
  dateFormat: string,
  requiredFormat: string
) => {
  return moment(date, dateFormat).format(requiredFormat)
}

export const getStartOfTheDate = (date: Date) => moment(date).startOf('day')

export const getEndOfTheDate = (date: Date) => moment(date).endOf('day')

export const subtractFromDate = (date: Date, amount: number) =>
  moment(date).subtract(amount, 'days').toDate()

export const formatMonth = (value: string) => {
  const shortMonthName = value.split(' ')[0].slice(0, 3)
  const restOfTheDate = value.split(' ').slice(1)
  return [shortMonthName, ...restOfTheDate].join(' ')
}

export const getTimeForActivity = (inputTime: string): string => {
  const now = new Date()
  const past = new Date(inputTime)
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000)

  const secondsInMinute = 60
  const secondsInHour = 60 * secondsInMinute
  const secondsInDay = 24 * secondsInHour

  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
    return date.toLocaleDateString(undefined, options)
  }

  if (diffInSeconds < secondsInMinute) {
    return `${diffInSeconds} seconds ago`
  } else if (diffInSeconds < secondsInHour) {
    const minutes = Math.floor(diffInSeconds / secondsInMinute)
    return `${minutes} ${minutes > 1 ? 'minutes' : 'minute'} ago`
  } else if (diffInSeconds < secondsInDay) {
    const hours = Math.floor(diffInSeconds / secondsInHour)
    return `${hours} ${hours > 1 ? 'hours' : 'hour'} ago`
  } else if (diffInSeconds < 2 * secondsInDay) {
    const days = Math.floor(diffInSeconds / secondsInDay)
    return `${days} ${days > 1 ? 'days' : 'day'} ago`
  } else {
    return formatDate(past)
  }
}

export const getTimeForActivityTimeZone = (
  inputTime: string,
  orgTimeZone: any
): string => {
  const currentDate = new Date()
  const past = new Date(inputTime)

  const pastTimeZone = new Date(
    timeZone(orgTimeZone, inputTime, timeFormats.fullMonthDateYearTime)
  )
  const diffInSeconds = Math.floor(
    (currentDate.getTime() - past.getTime()) / 1000
  )

  const secondsInMinute = 60
  const secondsInHour = 60 * secondsInMinute
  const secondsInDay = 24 * secondsInHour
  // TODO we need to modify using moment js
  const formatDate = (date: any): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
    return date.toLocaleDateString(undefined, options)
  }

  if (diffInSeconds < secondsInMinute) {
    return `${diffInSeconds} seconds ago`
  } else if (diffInSeconds < secondsInHour) {
    const minutes = Math.floor(diffInSeconds / secondsInMinute)
    return `${minutes} ${minutes > 1 ? 'minutes' : 'minute'} ago`
  } else if (diffInSeconds < secondsInDay) {
    const hours = Math.floor(diffInSeconds / secondsInHour)
    return `${hours} ${hours > 1 ? 'hours' : 'hour'} ago`
  } else if (diffInSeconds < 2 * secondsInDay) {
    const days = Math.floor(diffInSeconds / secondsInDay)
    return `${days} ${days > 1 ? 'days' : 'day'} ago`
  } else {
    return formatDate(pastTimeZone)
  }
}

export const getNotifiedTime = (inputTime: string): string => {
  const now = new Date()
  const past = new Date(inputTime)
  const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000)

  const secondsInMinute = 60
  const secondsInHour = 60 * secondsInMinute
  const secondsInDay = 24 * secondsInHour

  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
    return date.toLocaleDateString(undefined, options)
  }

  if (diffInSeconds < secondsInMinute) {
    return `${diffInSeconds} s ago`
  } else if (diffInSeconds < secondsInHour) {
    const minutes = Math.floor(diffInSeconds / secondsInMinute)
    return `${minutes}m ago`
  } else if (diffInSeconds < secondsInDay) {
    const hours = Math.floor(diffInSeconds / secondsInHour)
    return `${hours}h ago`
  } else if (diffInSeconds < 2 * secondsInDay) {
    const days = Math.floor(diffInSeconds / secondsInDay)
    return `${days}d ago`
  } else {
    return formatDate(past)
  }
}

export const getOrgTimeRange = (orgTimeZone = 'UTC') => {
  const startOfMonth = new Date(
    moment()
      .tz(orgTimeZone)
      .startOf('month')
      .format(timeFormats.dateWithHoursAndMinutesAndSeconds)
  ).valueOf()
  const endOfCurrentDayTimestamp = new Date(
    moment()
      .tz(orgTimeZone)
      .endOf('day')
      .format(timeFormats.dateWithHoursAndMinutesAndSeconds)
  ).valueOf()
  return {
    startOfMonth,
    endOfCurrentDayTimestamp
  }
}

export const getOrgTimeRangeForLast7DaysEndOfDay = (orgTimeZone = 'UTC') => {
  const now = moment().tz(orgTimeZone)

  // Start: 7 days ago at start of day (00:00:00)
  const startOf7DaysAgo = new Date(
    now
      .clone()
      .subtract(6, 'days')
      .startOf('day')
      .format(timeFormats.dateWithHoursAndMinutesAndSeconds)
  ).valueOf()

  // End: End of today (23:59:59.999)
  const endOfCurrentDayTimestamp = new Date(
    now
      .clone()
      .endOf('day')
      .format(timeFormats.dateWithHoursAndMinutesAndSeconds)
  ).valueOf()

  return {
    startOf7DaysAgo,
    endOfCurrentDayTimestamp
  }
}

interface Filters {
  'created-from': string
  'created-to': string
  'time-zone': string
}

export const getCommonFilters = (
  response: Record<string, any>,
  startOfMonth: number,
  endOfCurrentDayTimestamp: number,
  timeZone = 'UTC'
): Filters => {
  return {
    'created-from': moment
      .tz(response.filters?.['created-from'] || startOfMonth, timeZone)
      .toISOString(),
    'created-to': moment
      .tz(
        response.filters?.['created-to'] || endOfCurrentDayTimestamp,
        timeZone
      )
      .toISOString(),
    'time-zone': timeZone
  }
}

export const getUserTimeZoneDate = (
  timestamp: Date | string,
  userTimeZone: string
) => {
  try {
    return moment(timestamp)
      .tz(userTimeZone)
      .format(timeFormats.dayMonthDateYearTimeWithMeridiemZoneOffset)
  } catch (error) {
    console.error('Error formatting timezone:', error)
    return getTimeForActivity(String(timestamp))
  }
}

export const formatTimestampByTimezone = (
  utcTimestamp: string,
  timeZoneCode: string
) => {
  const date = new Date(utcTimestamp)

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZoneCode
  })

  const formattedDateTime = formatter.format(date)
  return formattedDateTime
}

/**
 * Get date range for the last N days in organization timezone
 * @param {number} days - Number of days to go back (7, 14, 30, etc.)
 * @param {string} orgTimeZone - Organization timezone (default: 'UTC')
 * @returns {DateRange} - Date range object with created-from, created-to, and time-zone
 */
export const getLastNDaysRange = (
  days: number,
  orgTimeZone = 'UTC'
): Filters => {
  const today = moment().tz(orgTimeZone)

  // End date: yesterday end of day
  const endDate = today.clone().subtract(1, 'day')
  const createdTo = endDate.endOf('day').toISOString()

  // Start date: N days before end date (counting end date as day 1)
  const createdFrom = endDate
    .clone()
    .subtract(days - 1, 'days')
    .startOf('day')
    .toISOString()

  return {
    'created-from': createdFrom,
    'created-to': createdTo,
    'time-zone': orgTimeZone
  }
}
